import {
  Component,
  Dispatch,
  FC,
  lazy,
  memo,
  ReactNode,
  SetStateAction,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslate } from 'react-polyglot'
import { HtmlPortalNode, OutPortal } from 'react-reverse-portal'
import { Link, useLocation, useRouteMatch } from 'react-router-dom'
import { useAsync } from 'react-use'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { ScreenPlayOnOutlinedIcon } from '@getgo/chameleon-icons/react'
import {
  BellFilledIcon,
  BellOutlinedIcon,
  HelpCircleFilledIcon,
  HelpCircleOutlinedIcon,
  LaunchFilledIcon,
  LaunchOutlinedIcon,
  ShoppingCartFilledIcon,
  ShoppingCartOutlinedIcon,
  WrenchOutlinedIcon,
} from '@getgo/chameleon-icons/react'
import { PopoverComponent } from '@getgo/chameleon-web'
import { Button, IconButton, Popover, Skeleton, Tooltip, Typography } from '@getgo/chameleon-web-react-wrapper'
import { AppBar, Badge, Box, Grid, makeStyles, Theme, Toolbar } from '@material-ui/core'
import useLocalStorage from '@rehooks/local-storage'
import { captureException } from '@sentry/react'
import { useMessaging } from '@ucc/admin-messaging'
import { PrivilegedContent } from '@ucc/unified-admin-common'
import mixpanel from 'mixpanel-browser'

import GoToAdminBlackYellow from 'assets/goto_admin_Black_Yellow.svg'
import GoToAdminWhiteYellow from 'assets/goto_admin_White_Yellow.svg'
import { Divider, OrganizationContext } from 'components'
import { useAccountContext } from 'components/contexts/AccountContext'
import useBillingFeatures from 'hooks/useBillingFeatures'
import useHasPartnerAccess from 'hooks/useHasPartnerAccess'
import useIsUserSystemUser from 'hooks/useIsUserSystemUser'
import useLocale from 'hooks/useLocale'
import useSearchBarMode, { SearchBarMode } from 'hooks/useSearchBarMode'
import { Activity } from 'types/Activity'
import { CartInfo } from 'types/Cart'
import { CopilotLegalTermsStatus } from 'types/CopilotLegalTerms'
import { DynamicSetupConfig, SetupType } from 'types/DynamicSetupConfig'
import { mixpanelEventNames } from 'utils'
import { getProductFamilyName } from 'utils'
import { DEFAULT_LOCALE, HOME_KEY, SHOW_ADMIN_TOUR_POPOVER } from 'utils/constants'
import { CART_UPDATE_EVENT } from 'utils/messaging/events'
import { Privileges } from 'utils/rolesToPrivilegesMapper'
import { getCartInfo } from 'utils/ucs/fetchCart'
import AccountSwitcher from './accountMenu/AccountSwitcher'
import AppSwitcher from './AppSwitcher'
import AskCopilotButton from './copilot/AskCopilotButton'
import GlobalSearchBar from './globalSearch/GlobalSearchBar'
import { SetupDrawerMode } from './Layout'
import DynamicSetup from './layout/DynamicSetup'
import { SideDrawerMode } from './layout/SideDrawer'
import PbxSwitcher from './pbxSwitcher/PbxSwitcher'
import { DefaultPageContext } from './providers/DefaultPageProvider'
import { UnleashContext } from './providers/UnleashProvider'
import TopBarBanner from './TopBarBanner'

// Lazy imports
const Profile = lazy(() => import('./Profile'))

const useStyles = makeStyles((theme: Theme) => ({
  toolbar: {
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: tokens.surfaceColorDefault,
    paddingLeft: `${tokens.spacing04} !important`,
    paddingRight: `${tokens.spacing04} !important`,
  },
  appBar: {
    borderBottom: `1px solid ${tokens.borderColorDefault}`,
    zIndex: theme.zIndex.drawer + 1,
    top: 0,
  },
  logoLink: {
    width: '142px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
  },
  brandText: { paddingLeft: '15px', p: { fontWeight: 600, fontSize: 20 } },
  goToLogo: {
    '--goto-icon-size': '65px',
  },
  tertiaryButton: {
    backgroundColor: 'var(--goto-interactive-color-secondary)',
  },
  tertiaryButtonHover: {
    backgroundColor: 'var(--goto-interactive-color-secondary-hover)',
  },
  resumeAdminTourPopOver: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '15px',
    width: '400px',
  },
}))

interface TopBarIconButtonProps {
  title?: string
  onToggle?: () => void
  activeIcon?: ReactNode
  open?: boolean
  inactiveIcon?: ReactNode
  trailingText?: string
  isDisable?: boolean
}

const TopBarIconButton = ({
  title,
  open,
  onToggle,
  activeIcon,
  inactiveIcon,
  isDisable = false,
}: TopBarIconButtonProps) => (
  <>
    <IconButton
      id="topbar-icon"
      variant={open ? 'secondary' : 'primary'}
      size="medium"
      onClick={onToggle}
      disabled={isDisable}
      role="button"
    >
      {open ? activeIcon : inactiveIcon}
    </IconButton>

    <Tooltip triggerId="topbar-icon">
      <span> {title || ''}</span>
    </Tooltip>
  </>
)

interface TopBarProps {
  activities?: Activity[]
  chameleonTheme?: 'light' | 'dark'
  hasBanner?: boolean
  setupDrawerMode: SetupDrawerMode
  onSelect: Dispatch<SetStateAction<string>>
  onSelectSubTab: (selectedRoute: string, selectedRouteSubSelection: string) => void
  setUpgradeBannerOpen?: (state: boolean) => void
  sideDrawerState: SideDrawerMode
  onUpdateSideDrawerMode: (mode: SideDrawerMode) => void
  dynamicSetupConfig?: DynamicSetupConfig
  copilotPortalNode?: HtmlPortalNode<Component<any>>
  isCopilotPin?: boolean
  isCopilotOpen?: boolean
  onCopilotClick?: (isOpen: boolean) => void
}

const TopBar: FC<TopBarProps> = ({
  activities,
  chameleonTheme,
  hasBanner,
  setupDrawerMode,
  onSelect,
  onSelectSubTab,
  setUpgradeBannerOpen,
  sideDrawerState,
  onUpdateSideDrawerMode,
  dynamicSetupConfig,
  copilotPortalNode,
  isCopilotPin = false,
  isCopilotOpen,
  onCopilotClick,
}) => {
  const classes = useStyles()
  const t = useTranslate()
  const { url } = useRouteMatch()
  const location = useLocation()
  const { isPartner, accountKey } = useAccountContext()
  const { isPartnerUser } = useHasPartnerAccess()
  const { isUserSystemUser } = useIsUserSystemUser()
  const pbx = useContext(OrganizationContext)
  const locale = useLocale()
  const [cartInfo, setCartInfo] = useState<CartInfo | undefined>(undefined)
  const searchBarState = useSearchBarMode()
  const { isBillingAdmin, partnerHasChildBillingAccess, systemUserHasBillingAccess } = useBillingFeatures() || {}
  const { searchAccountsDropdownEnabled } = useContext(UnleashContext)
  const { defaultPage } = useContext(DefaultPageContext)

  const [showAdminTourPopOver, setShowAdminTourPopOver] = useLocalStorage<boolean>(SHOW_ADMIN_TOUR_POPOVER)
  const [localDynamicSetupConfig, setLocalDynamicSetupConfig] = useState(dynamicSetupConfig)
  const adminTourPopoverRef = useRef<HTMLElement | any>(null)
  const isDefaultLanguage = locale === DEFAULT_LOCALE

  useEffect(() => {
    setLocalDynamicSetupConfig(dynamicSetupConfig)
  }, [dynamicSetupConfig])

  const onCompleteDynamicSetup = useCallback(() => {
    if (localDynamicSetupConfig) {
      setLocalDynamicSetupConfig({ ...localDynamicSetupConfig, completed: true })
    }
  }, [localDynamicSetupConfig])

  const toggleNotificationsDrawer = () => {
    onUpdateSideDrawerMode(
      sideDrawerState === SideDrawerMode.notifications ? SideDrawerMode.none : SideDrawerMode.notifications,
    )

    mixpanel.track(mixpanelEventNames.NOTIFICATIONS_DRAWER, {
      Action: `Toggled notifications drawer`,
    })
  }

  const togglePartnerToolsDrawer = () => {
    onUpdateSideDrawerMode(
      sideDrawerState === SideDrawerMode.partnerTools ? SideDrawerMode.none : SideDrawerMode.partnerTools,
    )
    mixpanel.track(mixpanelEventNames.PARTNER_TOOLS_DRAWER, {
      Action: `Toggled partner tools drawer`,
    })
  }

  const toggleSetupDrawer = () => {
    onUpdateSideDrawerMode(sideDrawerState === SideDrawerMode.setup ? SideDrawerMode.none : SideDrawerMode.setup)
    mixpanel.track(mixpanelEventNames.SETUP_DRAWER, {
      Action: `Toggled setup drawer`,
    })
  }

  const toggleResolveSetupDrawer = () => {
    onUpdateSideDrawerMode(
      sideDrawerState === SideDrawerMode.setupResolve ? SideDrawerMode.none : SideDrawerMode.setupResolve,
    )
    mixpanel.track(mixpanelEventNames.SETUP_DRAWER, {
      Action: `Toggled Resolve setup drawer`,
    })
  }

  const toggleHelpDrawer = () => {
    onUpdateSideDrawerMode(sideDrawerState === SideDrawerMode.help ? SideDrawerMode.none : SideDrawerMode.help)
    mixpanel.track(mixpanelEventNames.HELP_DRAWER, {
      Action: `Toggled help drawer`,
    })
  }

  const toggleCartDrawer = () =>
    onUpdateSideDrawerMode(sideDrawerState === SideDrawerMode.cart ? SideDrawerMode.none : SideDrawerMode.cart)

  const toggleCopilotDrawer = () => {
    onUpdateSideDrawerMode(sideDrawerState === SideDrawerMode.copilot ? SideDrawerMode.none : SideDrawerMode.copilot)
    onCopilotClick && onCopilotClick(!isCopilotOpen)

    mixpanel.track(mixpanelEventNames.COPILOT_DRAWER, {
      Action: 'Toggled copilot drawer',
    })
  }

  const handleOnPopOverChange = () => {
    const popover = adminTourPopoverRef.current as PopoverComponent
    if (popover != null && !popover.isOpen) {
      setShowAdminTourPopOver(false)
    }
  }
  const handleGotItButtonClick = () => {
    const popover = adminTourPopoverRef.current as PopoverComponent
    if (popover.isOpen) {
      popover.close && popover.close()
    } else {
      popover.open && popover.open()
    }
  }

  useEffect(() => {
    const popover = adminTourPopoverRef.current as PopoverComponent
    if (popover) {
      if (showAdminTourPopOver) {
        popover.open && popover.open()
      } else {
        popover.close && popover.close()
      }
    }
  }, [showAdminTourPopOver])

  const openAdminCopilot = () => {
    pbx?.onCopilotLegalTermsAgreements && pbx?.onCopilotLegalTermsAgreements(CopilotLegalTermsStatus.UNREVIEWED)

    onCopilotClick && onCopilotClick(true)
    onUpdateSideDrawerMode(isCopilotPin ? SideDrawerMode.copilot : SideDrawerMode.none)
  }

  useAsync(async () => {
    if (accountKey && (isBillingAdmin || partnerHasChildBillingAccess || systemUserHasBillingAccess)) {
      try {
        const cartInfo = await getCartInfo(accountKey)
        setCartInfo(cartInfo)
      } catch (error) {
        captureException(error)
      }
    }
  }, [accountKey, isBillingAdmin, partnerHasChildBillingAccess, systemUserHasBillingAccess])

  useMessaging(CART_UPDATE_EVENT, (payload: unknown) => {
    const { cartInfo, isSidebarOpened } = payload as { cartInfo?: CartInfo; isSidebarOpened: boolean }
    cartInfo && setCartInfo(cartInfo)
    onUpdateSideDrawerMode(isSidebarOpened ? SideDrawerMode.cart : SideDrawerMode.none)
  })

  const disableCart = () => location.pathname.includes('cart') || location.pathname.includes('checkout')

  return (
    <AppBar className={classes.appBar} position={hasBanner ? 'sticky' : 'fixed'} elevation={0} color="default">
      <TopBarBanner setUpgradeBannerOpen={setUpgradeBannerOpen} />

      <Toolbar className={classes.toolbar} variant="dense">
        <Grid spacing={4} container alignItems="center" justifyContent="space-between" wrap="nowrap">
          <Grid item>
            <Grid container alignItems="center" wrap="nowrap">
              <Box display="flex">
                <Link
                  className={classes.logoLink}
                  to={`${url}/${defaultPage}`}
                  title="GoToAdmin"
                  onClick={() => {
                    onSelect(HOME_KEY)
                    onSelectSubTab(HOME_KEY, defaultPage)
                  }}
                >
                  {chameleonTheme === 'dark' ? <GoToAdminWhiteYellow /> : <GoToAdminBlackYellow />}
                </Link>
              </Box>

              <Divider orientation="vertical" flexItem style={{ marginLeft: 23, marginRight: 16 }} />
              {isUserSystemUser && searchAccountsDropdownEnabled && (
                <>
                  <PbxSwitcher />
                  <Divider orientation="vertical" flexItem style={{ marginLeft: 16, marginRight: 16 }} />
                </>
              )}
              <AccountSwitcher />
            </Grid>
          </Grid>

          <Grid item>
            <Grid style={{ width: 'auto' }} spacing={2} alignItems="center" container wrap="nowrap">
              {isDefaultLanguage && searchBarState === SearchBarMode.globalSearch && (
                <GlobalSearchBar openAdminCopilot={openAdminCopilot} />
              )}

              {searchBarState === SearchBarMode.copilot && copilotPortalNode && (
                <>
                  {isCopilotPin ? (
                    <AskCopilotButton isCopilotOpen={isCopilotOpen} onClick={toggleCopilotDrawer} />
                  ) : (
                    <>
                      <AskCopilotButton isCopilotOpen={isCopilotOpen} />
                      <Popover
                        compact
                        isOpen={isCopilotOpen}
                        triggerId="copilot-text-field"
                        onChange={(
                          event: Partial<
                            CustomEvent<{
                              isOpen: boolean
                            }>
                          >,
                        ) => {
                          mixpanel.track(mixpanelEventNames.COPILOT_DRAWER, {
                            Action: 'Toggled copilot drawer',
                          })
                          if (event.detail) {
                            onCopilotClick && onCopilotClick(event.detail.isOpen)
                          }
                        }}
                      >
                        <div style={{ width: 532 }}>
                          <OutPortal node={copilotPortalNode} onClose={() => onCopilotClick && onCopilotClick(false)} />
                        </div>
                      </Popover>
                    </>
                  )}
                </>
              )}

              {setupDrawerMode === SetupDrawerMode.setupConnect && (
                <PrivilegedContent privilege={Privileges.Setup}>
                  <Grid item id="setup-button" data-testid="setupButton">
                    <TopBarIconButton
                      title={t('Setup')}
                      open={sideDrawerState === SideDrawerMode.setup}
                      inactiveIcon={<LaunchOutlinedIcon />}
                      onToggle={toggleSetupDrawer}
                      activeIcon={<LaunchFilledIcon />}
                    />
                  </Grid>
                </PrivilegedContent>
              )}
              {localDynamicSetupConfig?.setupType === SetupType.DYNAMIC_SETUP && !localDynamicSetupConfig.completed && (
                <PrivilegedContent privilege={Privileges.Setup}>
                  <Grid item id="dynamic-setup-button" data-testid="dynamicSetupButton">
                    <DynamicSetup
                      pbx={pbx}
                      dynamicSetupConfig={localDynamicSetupConfig}
                      onCompleteSetup={onCompleteDynamicSetup}
                      isDefaultLanguage={isDefaultLanguage}
                    />
                  </Grid>
                </PrivilegedContent>
              )}
              {setupDrawerMode === SetupDrawerMode.setupResolve && (
                <PrivilegedContent privilege={Privileges.SetupG2R}>
                  <Grid item id="resolve-setup-button" data-testid="resolveSetupButton">
                    <TopBarIconButton
                      title={t('Set up %{resolveTitle}', {
                        resolveTitle: getProductFamilyName('gotoresolve'),
                      })}
                      open={sideDrawerState === SideDrawerMode.setupResolve}
                      inactiveIcon={<LaunchOutlinedIcon />}
                      onToggle={toggleResolveSetupDrawer}
                      activeIcon={<LaunchFilledIcon />}
                    />
                  </Grid>
                </PrivilegedContent>
              )}

              {(isPartner || isPartnerUser) && (
                <Grid item id="partner-tools-button" data-testid="partner-tools-button">
                  <Box display="flex">
                    <Button
                      variant="tertiary"
                      className={
                        sideDrawerState === SideDrawerMode.partnerTools
                          ? classes.tertiaryButtonHover
                          : classes.tertiaryButton
                      }
                      leadingIcon={<WrenchOutlinedIcon />}
                      onClick={togglePartnerToolsDrawer}
                    >
                      {t('Partner tools')}
                    </Button>
                  </Box>
                </Grid>
              )}

              {(isBillingAdmin || partnerHasChildBillingAccess || systemUserHasBillingAccess) && (
                <Grid item id="cart-button" data-testid="cartButton">
                  <Badge
                    badgeContent={cartInfo?.cartItems?.length || 0}
                    color="primary"
                    overlap="circular"
                    variant="dot"
                  >
                    <TopBarIconButton
                      title={t('Go to cart')}
                      open={sideDrawerState === SideDrawerMode.cart}
                      inactiveIcon={<ShoppingCartOutlinedIcon />}
                      onToggle={toggleCartDrawer}
                      activeIcon={<ShoppingCartFilledIcon />}
                      isDisable={disableCart()}
                    />
                  </Badge>
                </Grid>
              )}

              <Grid item id="notifications-button">
                <Badge badgeContent={activities?.length || 0} color="primary" overlap="circular" variant="dot">
                  <TopBarIconButton
                    title={t('Notifications')}
                    open={sideDrawerState === SideDrawerMode.notifications}
                    onToggle={toggleNotificationsDrawer}
                    inactiveIcon={<BellOutlinedIcon />}
                    activeIcon={<BellFilledIcon />}
                  />
                </Badge>
              </Grid>
              <Grid item id="help-button" data-testid="helpButton">
                <TopBarIconButton
                  title={t('Help')}
                  open={sideDrawerState === SideDrawerMode.help}
                  onToggle={toggleHelpDrawer}
                  activeIcon={<HelpCircleFilledIcon />}
                  inactiveIcon={<HelpCircleOutlinedIcon />}
                />
              </Grid>
              <Popover
                hidden={!showAdminTourPopOver}
                ref={adminTourPopoverRef}
                position="bottom-end"
                arrow
                isOpen={!!showAdminTourPopOver}
                onChange={handleOnPopOverChange}
                data-testid={'popover'}
                triggerId="help-button"
              >
                <Box className={classes.resumeAdminTourPopOver}>
                  <ScreenPlayOnOutlinedIcon size="32px" />
                  <Typography variant="body-medium-strong" color="type-color-default">
                    {t('Pick up where you left off')}
                  </Typography>
                  <Typography variant="body-medium" color="type-color-default">
                    {t('You’ll find the GoTo Admin quick tour under the help menu if you want to pick up again later.')}
                  </Typography>
                  <Button
                    onClick={() => {
                      handleGotItButtonClick()
                      setShowAdminTourPopOver(false)
                    }}
                  >
                    {t('Got it')}
                  </Button>
                </Box>
              </Popover>

              <Grid item id="app-switcher-button">
                <AppSwitcher />
              </Grid>

              <Grid item id="profile-button">
                <Suspense
                  fallback={
                    <Box display="flex" mt={'1px'}>
                      <Skeleton size="medium" variant="circle" />
                    </Box>
                  }
                >
                  <Box display="flex">
                    <Profile />
                  </Box>
                </Suspense>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  )
}

export default memo(TopBar)
